import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/**
 * Floating "enable two-factor authentication" reminder shown on the dashboard.
 *
 * Behaviour (per user spec):
 *   - Visible only when the current user has `two_factor_enabled === false`.
 *   - Shows once per calendar day per user (dismiss → suppressed for the rest of the day).
 *   - The moment the user enables 2FA in Settings (which dispatches `nextbell:2fa-changed`),
 *     this banner disappears immediately.
 *   - If they later disable 2FA, the banner reappears the next day (or after a manual refresh).
 *   - Renders bottom-right on desktop, bottom-center on mobile, above the safe-area inset.
 */
export default function TwoFactorReminder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  const enabled = !!user?.two_factor_enabled;
  const userId = user?.id;

  // Compute the once-per-day storage key; rotates at midnight local time.
  const todayKey = userId ? `nextbell:2fa-reminder-dismissed:${userId}:${new Date().toISOString().slice(0, 10)}` : null;

  useEffect(() => {
    if (!userId || enabled) {
      setVisible(false);
      return undefined;
    }
    let dismissed = false;
    try { dismissed = !!localStorage.getItem(todayKey); } catch { /* ignore */ }
    if (!dismissed) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [userId, enabled, todayKey]);

  // If the user just enabled 2FA in Settings, hide instantly.
  useEffect(() => {
    const onChange = () => setVisible(false);
    window.addEventListener('nextbell:2fa-changed', onChange);
    return () => window.removeEventListener('nextbell:2fa-changed', onChange);
  }, []);

  const dismiss = () => {
    try { if (todayKey) localStorage.setItem(todayKey, '1'); } catch { /* ignore */ }
    setVisible(false);
  };

  const goEnable = () => {
    dismiss();
    navigate('/dashboard/settings?tab=profile#two-factor');
  };

  if (!visible) return null;

  return (
    <div
      data-testid="2fa-reminder-toast"
      className="fixed z-[60] bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm pointer-events-none"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="pointer-events-auto bg-white dark:bg-slate-900 border border-gray-200 dark:border-white/10 shadow-2xl rounded-2xl p-4 sm:p-5 backdrop-blur-xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/30 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Secure your account</p>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 leading-relaxed">
              Turn on two-factor authentication to get an email code on every login. Takes 10 seconds.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button data-testid="2fa-reminder-enable-btn" onClick={goEnable}
                className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-full px-3.5 py-1.5">
                Turn it on
              </button>
              <button data-testid="2fa-reminder-later-btn" onClick={dismiss}
                className="text-xs text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white px-2 py-1.5">
                Later
              </button>
            </div>
          </div>
          <button onClick={dismiss} aria-label="Dismiss" className="text-gray-400 dark:text-slate-500 hover:text-gray-900 dark:hover:text-white -mr-2 -mt-2 p-2.5 flex-shrink-0 rounded-full hover:bg-gray-50 dark:hover:bg-white/[0.05]">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
